<script setup lang="ts">
  import { mixed, object, string } from 'yup'
  import { ProfileStore } from '~/stores/profile'
  import { PlanType } from '~/generated/api-clients-generated'
  import { EngineStore } from '~/stores/engine'
  import { SessionStore } from '~/stores/session'
  import { isEmail, isPhoneNumber } from '~/utils/validation'

  const { getId } = Id.use('push-to-cs-review-button')
  const { track: rdTrack } = Analytics.use()
  const { createCsClient } = PxApi.use()
  const { isAuthenticated, signUp } = Auth.use()
  const { path, route } = AppRoute.use()
  const { show } = DialogController.use()
  const { getComponentContent } = Cms.use()
  const { content: detailsContent } = await getComponentContent('PushToCsReview', 'Details')
  const { content: compareContent } = await getComponentContent('PushToCsReview', 'Compare')
  const { isSupportHours } = await SupportHours.use()
  const profile = ProfileStore.use()
  const engine = EngineStore.use()
  const session = SessionStore.use()

  const loading = ref<boolean>(false)
  const errorState = ref<boolean>(false)
  const submitted = ref<boolean>(false)

  const messageName = ref<string | null>(null)
  const messageContact = ref<string | null>(null)
  const message = ref<string | null>(null)

  const context = computed(() => (path.value.includes('/compare') ? 'compare' : 'details'))
  const content = computed(() =>
    context.value === 'compare' ? compareContent.value : detailsContent.value
  )
  const modalId = computed(() => getId.value('contact-modal'))
  const planType = computed(() => engine.selectedPlanType)

  const contextData = computed(() => ({
    source: path.value,
    planType: planType.value ? PlanType[planType.value] : undefined
  }))

  const viewedPlans = computed(() => {
    if (context.value === 'compare') {
      return session.comparedPlans.join(',')
    }
    if (context.value === 'details') {
      const planId = (route.params.path as string[])?.[1]?.toUpperCase()
      return planId ?? undefined
    }
    return undefined
  })

  const analyticsProps = computed(() => ({
    ...contextData.value,
    duringSupportHours: isSupportHours.value
  }))

  const customerPhone = computed(() =>
    isPhoneNumber(messageContact.value) ? messageContact.value : profile.primaryContactPhone?.number
  )

  const customerEmail = computed(() =>
    isEmail(messageContact.value) ? messageContact.value : profile.emailAddress?.address
  )

  const displayCustomerEmail = computed(() => customerEmail.value?.replace('@', '<wbr>@<wbr>'))

  const validationSchema = computed(() =>
    object({
      'cs-review-name': string()
        .required('Please enter a name')
        .matches(/^[A-Za-z'\s-]+$/, 'Please enter a valid name'),
      'cs-review-contact': mixed()
        .required('Please enter a contact method')
        .test(
          'cs-review-contact',
          'Please enter a valid email or phone',
          () => isEmail(messageContact.value) || isPhoneNumber(messageContact.value)
        ),
      'cs-review-message': string().required('Please let us know how we can help')
    })
  )

  const track = (event: string) => {
    rdTrack(event, { ...analyticsProps.value })
  }

  const onReviewClicked = () => {
    track('CsReviewClicked')
    if (isSupportHours.value) {
      PxChat.show({ pxSource: 'pushToCsReview' })
      return
    }
    show(modalId.value)
  }

  const reset = () => {
    messageName.value = null
    messageContact.value = null
    message.value = null
    errorState.value = false
    submitted.value = false
  }

  const onMessageSubmit = async () => {
    try {
      errorState.value = false
      loading.value = true

      const client = createCsClient()
      await client.sendMessage({
        event: 'CSPushToReview',
        attributes: {
          profileId: profile.id ?? undefined,
          customerName: messageName.value ?? undefined,
          customerPhone: customerPhone.value ?? undefined,
          customerEmail: customerEmail.value ?? undefined,
          userMessage: message.value ?? undefined,
          description: `Context: ${context.value}`,
          viewedPlans: viewedPlans.value,
          ...contextData.value
        }
      })

      track('CsReviewMessageSubmit')
      submitted.value = true
    } catch (ex) {
      errorState.value = true
    } finally {
      loading.value = false
    }

    try {
      if (!isAuthenticated.value && !_isNil(customerEmail.value)) {
        const [first, ...rest] = messageName.value!.split(' ')

        await signUp(true, {
          firstName: first,
          lastName: rest.pop() ?? '',
          identifier: customerEmail.value!,
          profileId: profile.id!
        })

        await profile.load()
      }
    } catch (ex) {
      console.log('Error in soft account creation: ', ex)
    }
  }
</script>

<template>
  <HpButton
    @click="onReviewClicked"
    icon="lucide:user-star"
    variant="minimal"
    class="relative w-fit !rounded-full !bg-sky-900 !px-3 !text-white [&_svg]:!mr-1"
  >
    <span class="hidden tracking-tight md:flex">{{ content?.ext?.primary_button_text }}</span>
    <span class="hidden xs:flex md:hidden">Review With Agent</span>
    <span class="flex xs:hidden">Ask an Agent</span>
  </HpButton>

  <HpModal
    :title="submitted ? '' : content?.ext?.modal_title"
    :id="modalId"
    hide-footer
    @hidden="reset"
    no-padding
  >
    <section v-if="submitted" class="flex flex-col items-center gap-4 py-4 text-center">
      <SiteSuccessAnimatedCheckmark />
      <h3>{{ content?.ext?.success_heading }}</h3>
      <p class="text-balance leading-relaxed">{{ content?.ext?.success_text }}</p>
      <p
        v-if="customerEmail"
        class="mt-2 text-balance rounded-3xl bg-yellow-300 p-4 text-lg leading-relaxed"
      >
        {{ content?.ext?.success_message }}
        <span v-html="displayCustomerEmail" class="font-semibold"></span>.
      </p>
    </section>

    <div v-else class="flex w-full flex-col items-center gap-3">
      <p class="text-balance leading-relaxed">
        {{ content?.ext?.email_prompt }}
      </p>
      <HpForm
        class="flex flex-col gap-4"
        :validation-schema="validationSchema"
        :on-submit="onMessageSubmit"
      >
        <HpFormInput
          id="cs-review-name"
          v-model="messageName"
          :label="content?.ext?.email_name_label"
          :placeholder="content?.ext?.email_name_label"
        />
        <HpFormInput
          id="cs-review-contact"
          v-model="messageContact"
          :label="content?.ext?.email_contact_label"
          :placeholder="content?.ext?.email_contact_label"
        />
        <HpFormTextArea
          id="cs-review-message"
          v-model="message"
          :label="content?.ext?.email_message_label"
          :placeholder="content?.ext?.email_message_label"
        />
        <p class="text-balance py-2 leading-snug">
          {{ content?.ext?.email_footer }}
        </p>
        <p
          v-if="errorState"
          class="my-4 rounded-button border border-danger-dark bg-danger-light text-danger-dark"
        >
          An error has occurred. Please try again later.
        </p>
        <HpButton :busy="loading">
          {{ content?.ext?.email_send_button_text }}
        </HpButton>
      </HpForm>
    </div>
  </HpModal>
</template>

<style scoped></style>
